import { SparklesIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { DashboardInterviewStatusChip } from "@/components/dashboard/seven-day-schedule/DashboardInterviewStatusChip";
import { Avatar } from "@/components/quick-access/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import {
  interviewTypeKindClasses,
  shortTimezoneLabel,
  timezoneToIana,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import { DashboardInterview } from "@/types";

enum CalendarCardHeight {
  TINY = 16, // under ~20 min — shrink the text
  COMPACT = 48, // under 1 hour — single row; 1 hour+ splits name/time onto two rows
}

export const DashboardScheduleCard = ({
  interview,
  className,
  onSelect,
  calendarHeightPx,
  isCondensed = false,
  showOwnerAvatar = false,
}: {
  interview: DashboardInterview;
  className?: string;
  onSelect?: () => void; // Sidebar body only — fires from the candidate name, not the whole card
  calendarHeightPx?: number; // Expanded calendar - height of card
  isCondensed?: boolean; // Narrow column (overlap) — hide the avatar + sparkle
  showOwnerAvatar?: boolean;
}) => {
  const { userInitialsMap } = useTaggableUsers();
  const kind = interviewTypeKindClasses[interview.type];
  const isCalendar = calendarHeightPx != null;
  const isCompact =
    isCalendar && calendarHeightPx < Number(CalendarCardHeight.COMPACT);
  const isTiny =
    isCalendar && calendarHeightPx < Number(CalendarCardHeight.TINY);
  const ownerInitials =
    showOwnerAvatar && interview.ownerUserId != null
      ? (userInitialsMap[interview.ownerUserId] ?? "?")
      : null;

  const renderBody = () => {
    if (!isCalendar) {
      return (
        <SidebarCardBody
          interview={interview}
          ownerInitials={ownerInitials}
          onSelect={onSelect}
        />
      );
    }
    if (isCompact) {
      return (
        <CompactEventBody
          interview={interview}
          ownerInitials={ownerInitials}
          isTiny={isTiny}
          isCondensed={isCondensed}
        />
      );
    }
    return (
      <LargerEventBody
        interview={interview}
        ownerInitials={ownerInitials}
        isCondensed={isCondensed}
      />
    );
  };

  const render = () => (
    <Row
      className={cn(
        "min-w-0 items-stretch overflow-hidden rounded-lg border border-black/[0.06]",
        kind.cardBg,
        className,
      )}
    >
      <div
        className={cn(
          "shrink-0",
          isTiny ? "w-px" : isCalendar ? "w-0.5" : "w-1",
          kind.stripe,
        )}
      />
      <Column
        className={cn(
          "min-w-0 flex-1",
          isCalendar
            ? isCompact
              ? "justify-center px-1.5"
              : "justify-start gap-0.5 px-1.5 py-1"
            : "gap-0.5 py-2.5 pl-2.5 pr-2.5",
        )}
      >
        {renderBody()}
      </Column>
    </Row>
  );

  return render();
};

const AgentSparkle = ({
  interview,
  isCalendar,
}: {
  interview: DashboardInterview;
  isCalendar: boolean;
}) =>
  interview.isStafferAgentScheduled ? (
    <SparklesIcon
      className={cn(
        "shrink-0 text-blue-400",
        isCalendar ? "h-2.5 w-2.5" : "h-3 w-3",
      )}
    />
  ) : null;

const OwnerAvatar = ({
  initials,
  isCalendar,
}: {
  initials: string | null;
  isCalendar: boolean;
}) =>
  initials == null ? null : (
    <div className={cn("shrink-0", isCalendar && "origin-right scale-[0.65]")}>
      <Avatar initials={initials} />
    </div>
  );

const TypeChip = ({ interview }: { interview: DashboardInterview }) => {
  const kind = interviewTypeKindClasses[interview.type];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-full px-1.5 py-px text-[10px] font-medium leading-tight",
        kind.chip,
      )}
    >
      {kind.label}
    </span>
  );
};

const CompactEventBody = ({
  interview,
  ownerInitials,
  isTiny,
  isCondensed,
}: {
  interview: DashboardInterview;
  ownerInitials: string | null;
  isTiny: boolean;
  isCondensed: boolean;
}) => {
  const kind = interviewTypeKindClasses[interview.type];
  const candidateName = formatting.fullName(
    interview.candidacy?.firstName,
    interview.candidacy?.lastName,
  );

  return (
    <Row className="min-w-0 items-center justify-between gap-1">
      <Text
        truncate
        className={cn(
          "min-w-0 flex-1 font-semibold leading-none",
          isTiny ? "text-[9px]" : "text-[10px]",
          kind.title,
        )}
      >
        {candidateName}, {dates.formatTimeShort(new Date(interview.dateTime))}
      </Text>
      {isCondensed ? null : (
        <Row className="shrink-0 items-center gap-0.5">
          <OwnerAvatar initials={ownerInitials} isCalendar />
          <AgentSparkle interview={interview} isCalendar />
        </Row>
      )}
    </Row>
  );
};

const LargerEventBody = ({
  interview,
  ownerInitials,
  isCondensed,
}: {
  interview: DashboardInterview;
  ownerInitials: string | null;
  isCondensed: boolean;
}) => {
  const kind = interviewTypeKindClasses[interview.type];
  const candidateName = formatting.fullName(
    interview.candidacy?.firstName,
    interview.candidacy?.lastName,
  );
  const timeRange = dates.formatTimeRangeShort(
    new Date(interview.dateTime),
    interview.durationMinutes,
  );

  return (
    <Column className="min-w-0 gap-0.5">
      <Row className="min-w-0 items-center justify-between gap-1">
        <Text
          truncate
          className={cn(
            "min-w-0 flex-1 text-[11px] font-semibold leading-tight",
            kind.title,
          )}
        >
          {candidateName}
        </Text>
        {isCondensed ? null : <AgentSparkle interview={interview} isCalendar />}
      </Row>
      <Row className="min-w-0 items-center justify-between gap-1">
        <Text
          truncate
          className="min-w-0 flex-1 text-[10px] font-medium tabular-nums leading-tight text-slate-700"
        >
          {timeRange}
        </Text>
        {isCondensed ? null : (
          <OwnerAvatar initials={ownerInitials} isCalendar />
        )}
      </Row>
    </Column>
  );
};

const SidebarCardBody = ({
  interview,
  ownerInitials,
  onSelect,
}: {
  interview: DashboardInterview;
  ownerInitials: string | null;
  onSelect?: () => void;
}) => {
  const kind = interviewTypeKindClasses[interview.type];
  const start = new Date(interview.dateTime);
  const candidateName = formatting.fullName(
    interview.candidacy?.firstName,
    interview.candidacy?.lastName,
  );
  const timeRange = dates.formatTimeRangeShort(
    start,
    interview.durationMinutes,
  );

  // Candidate's local start time, shown only when it differs from the viewer's.
  const altStartTime = (() => {
    if (!interview.timezone) return null;
    const iana = timezoneToIana[interview.timezone];
    if (iana === dates.viewerTimezoneIana()) return null;
    return `${dates.formatTimeShort(start, iana)} ${shortTimezoneLabel[interview.timezone]}`;
  })();
  const fullTimeLabel = altStartTime
    ? `${timeRange} (${altStartTime})`
    : timeRange;

  return (
    <Column className="min-w-0 gap-0.5">
      <Row className="min-w-0 items-center justify-between gap-2">
        <span
          onClick={onSelect}
          className={cn(
            "min-w-0 flex-1",
            onSelect && "cursor-pointer hover:underline",
          )}
        >
          <Text
            truncate
            className={cn("min-w-0 text-xs font-semibold", kind.title)}
          >
            {candidateName}
          </Text>
        </span>
        <AgentSparkle interview={interview} isCalendar={false} />
      </Row>
      <Row className="min-w-0 items-center justify-between gap-x-2">
        <Tooltip delayDuration={750}>
          <TooltipTrigger asChild>
            <span className="min-w-0 truncate text-xs font-medium tabular-nums leading-tight text-slate-700">
              {fullTimeLabel}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            {fullTimeLabel}
          </TooltipContent>
        </Tooltip>
        {ownerInitials == null ? (
          <TypeChip interview={interview} />
        ) : (
          <OwnerAvatar initials={ownerInitials} isCalendar={false} />
        )}
      </Row>
      <Text
        truncate
        className="text-[11px] font-normal leading-tight text-slate-400"
      >
        {interview.company.name} · {interview.job.title}
      </Text>
      <DashboardInterviewStatusChip
        companyId={interview.company.id}
        interviewId={interview.id}
        jobId={interview.job.id}
        status={interview.status}
        type={interview.type}
      />
    </Column>
  );
};
